import { useEffect, useRef, useState, type RefObject } from "react"

import { cn } from "~/lib/utils"

// The sticky left rail on a post: a jump list built by scanning the rendered
// body for `h2[id]` (see prose.tsx's H2) rather than a hand-written outline,
// so it can never drift out of sync with the actual sections. Reads as empty
// during SSR/first paint — the aside column's width is fixed by the parent
// grid either way, so it fades in without shifting the article.
//
// Below xl the <aside> that holds this is display:none, but the component
// still mounts and runs the scan + observer — cheap enough (a handful of h2s)
// that a matchMedia guard isn't worth the extra state.
//
// A static hairline runs the full height of the list, and a second, bolder
// bar (absolutely positioned) tracks the active link's own offsetTop/height
// and animates via `transform`, sliding along that line to mark the current
// section. The link text itself never changes font-weight — only color —
// so it can't reflow/shift when a section becomes active.

type Heading = { id: string; label: string }

export function TableOfContents({
  containerRef,
}: {
  containerRef: RefObject<HTMLElement | null>
}) {
  const [headings, setHeadings] = useState<Heading[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [indicator, setIndicator] = useState<{ top: number; height: number } | null>(null)
  const linkRefs = useRef<Record<string, HTMLAnchorElement | null>>({})

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const nodes = Array.from(
      container.querySelectorAll<HTMLHeadingElement>("h2[id]"),
    )
    setHeadings(nodes.map((n) => ({ id: n.id, label: n.textContent ?? "" })))

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length > 0) setActiveId(visible[0].target.id)
      },
      { rootMargin: "-120px 0px -70% 0px", threshold: 0 },
    )
    nodes.forEach((n) => observer.observe(n))

    return () => observer.disconnect()
  }, [containerRef])

  useEffect(() => {
    const link = activeId ? linkRefs.current[activeId] : null
    if (!link) return
    setIndicator({ top: link.offsetTop, height: link.offsetHeight })
  }, [activeId, headings])

  if (headings.length < 2) return null

  return (
    <nav aria-label="Table of contents" className="sticky top-[120px] pl-6">
      <p className="u-eyebrow">Contents</p>
      <div className="relative mt-5">
        <span aria-hidden className="absolute inset-y-0 left-0 w-px bg-line" />
        <span
          aria-hidden
          className={cn(
            "absolute left-0 w-0.5 rounded-full bg-forest [transition:transform_300ms_ease-out,opacity_150ms_ease-out]",
            indicator ? "opacity-100" : "opacity-0",
          )}
          style={{
            height: indicator ? `${indicator.height}px` : 0,
            transform: `translateY(${indicator?.top ?? 0}px)`,
          }}
        />
        <ol className="list-none space-y-4">
          {headings.map((h) => (
            <li key={h.id}>
              <a
                ref={(el) => {
                  linkRefs.current[h.id] = el
                }}
                href={`#${h.id}`}
                className={cn(
                  "block py-0.5 pl-3 text-[13px] leading-snug transition-colors",
                  activeId === h.id
                    ? "text-forest"
                    : "text-mute hover:text-ink",
                )}
              >
                {h.label}
              </a>
            </li>
          ))}
        </ol>
      </div>
    </nav>
  )
}
